import type { PageContext } from "@redotech/redo-model/agents/page-context";

/**
 * Compute the effective page context the Ask Redo transport sends to the
 * agent.
 *
 * - Not dismissed: the page's base context, with each pin's `pageData`
 *   merged on top of the base `pageData` (pins win on key collisions).
 * - Dismissed: the page-owned `pageDescription` and `pageData` are
 *   stripped — the agent stops seeing what page the merchant is on — but
 *   the rest of the base context (`preferredSkill`/`requiredSkill`,
 *   `suggestedQuestions`, `pageTitle`) keeps flowing, and pin-contributed
 *   `pageData` still merges. Dismissing the chip means "don't read over my
 *   shoulder", not "forget how to route" or "drop what I deliberately
 *   attached".
 *
 * `pins` is intentionally typed to the minimal shape this function reads so
 * the helper stays decoupled from `PinnedReference` in global-agent-context.
 */
export function resolveEffectivePageContext(
  base: PageContext | undefined,
  pins: readonly { pageData?: Record<string, unknown> }[],
  dismissed: boolean,
): PageContext | undefined {
  const effectiveBase =
    dismissed && base
      ? { ...base, pageDescription: undefined, pageData: undefined }
      : base;
  if (pins.length === 0) {
    return effectiveBase;
  }
  const mergedPageData: Record<string, unknown> = {
    ...effectiveBase?.pageData,
  };
  for (const ref of pins) {
    if (ref.pageData) {
      Object.assign(mergedPageData, ref.pageData);
    }
  }
  return { ...effectiveBase, pageData: mergedPageData };
}
